import React from "react";
import { Link } from "react-router-dom";
import { useTable, usePagination, useSortBy, useFilters } from "react-table"; // highlight-line
import { operativeProcedureTypes } from "../../helpers/constants";
import { parseHTML, userHasPermission } from "../../helpers/GlobalFunctions";
import { actions, surgeonMessages } from "../../helpers/messages";
import CustomModal from "../../shared/CustomModal";
import ErrorMsgBlockComponent from "../Error/ErrorMessageBlock";

const CaseListTable = ({
    columns,
    data,
    initialState,
    fetchData,
    actionErrorObj,
    loading,
    pageCount: controlledPageCount,
    totalCount,
    showPagination,
    disableColmSort,
    refreshList,
    filterObj,
    errorObj,
    actionSuccessMsg,
    changePageSize
}) => {
    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        prepareRow,
        pageOptions,
        pageCount,
        page,
        state: { pageIndex, pageSize, sortBy },
        gotoPage,
        previousPage,
        nextPage,
        setPageSize,
        canPreviousPage,
        canNextPage
    } = useTable(
        {
            columns,
            data,
            initialState,
            manualPagination: true,
            autoResetPage: false,
            manualSortBy: true,
            disableFilters: false,
            disableSortBy: disableColmSort ? disableColmSort : false,
            pageCount: controlledPageCount,
            totalCount: 0,
        },
        useFilters,
        useSortBy,
        usePagination
    );

    const prevFilterObj = React.useRef(filterObj);

    // Fetch data initially or after any user action that triggers a relod of the list
    React.useEffect(() => {
        fetchData({ pageIndex, pageSize, sortBy, pageOptions });
        // eslint-disable-next-line
    }, [fetchData, pageIndex, pageSize, sortBy]);

    /* istanbul ignore next  */
    React.useMemo(() => {
        if (refreshList) {
            // If there is only one row in a page (other than the first page), go to the previous page after delete
            // If not, then the usual list reload using current parameters
            // This is done to avoid a "no data found" page in this exact scenario
            if (data.length === 1 && pageIndex > 0) {
                return previousPage();
            } else {
                fetchData({ pageIndex, pageSize, sortBy, pageOptions });
            }
        }
        // eslint-disable-next-line
    }, [refreshList]);

    // Reset to the first page in case of change in any filter options
    /* istanbul ignore next  */
    React.useMemo(() => {
        if (typeof filterObj !== 'undefined' && JSON.stringify(prevFilterObj.current) !== JSON.stringify(filterObj)) {
            return gotoPage(0);
        }
        // eslint-disable-next-line
    }, [filterObj]);

    /**
     * @description Function to handle page size change and ripple it up to save it
     * @param {*} newPageSize Updated page size from the dropdown
     */
    const handleChangePageSize = (newPageSize) => {
        changePageSize(newPageSize);
        setPageSize(newPageSize)
    }
    /* istanbul ignore next  */
    return (
        <>
            {errorObj ? <ErrorMsgBlockComponent errorObject={errorObj} /> :
                <>
                    {(data && data.length) || loading ?
                        <>
                            {actionErrorObj ? <ErrorMsgBlockComponent errorObject={actionErrorObj} /> : null}
                            {actionSuccessMsg ? <div className="proms-success-msg"><p>{actionSuccessMsg}</p></div> : null}
                            <div className="table-contents">
                                <table {...getTableProps()}>
                                    <thead>
                                        {headerGroups.map((headerGroup) => {
                                            let { key, ...restHeaderGroupProps } = headerGroup.getHeaderGroupProps()
                                            return (
                                                <tr key={key} {...restHeaderGroupProps}>
                                                    {headerGroup.headers.map((column) => {
                                                        let { key, ...restColumn } = column.getHeaderProps(column.getSortByToggleProps())
                                                        // Add the sorting props to control sorting. For this example
                                                        // we can add them into the header props
                                                        return (
                                                            <th key={key} {...restColumn} title="">
                                                                {column.render("Header")}
                                                                {/* Add a sort direction indicator */}
                                                                <span>
                                                                    {column.isSorted ? (
                                                                        column.isSortedDesc ? (
                                                                            <svg className="bi bi-chevron-down" width="1.2em" height="1.2em" viewBox="0 0 16 16" fill="currentColor" xmlns="http://www.w3.org/2000/svg" >
                                                                                <path fillRule="evenodd" d="M1.646 4.646a.5.5 0 01.708 0L8 10.293l5.646-5.647a.5.5 0 01.708.708l-6 6a.5.5 0 01-.708 0l-6-6a.5.5 0 010-.708z" clipRule="evenodd" />
                                                                            </svg>
                                                                        ) : (
                                                                            <svg className="bi bi-chevron-up" width="1.2em" height="1.2em" viewBox="0 0 16 16" fill="currentColor" xmlns="http://www.w3.org/2000/svg" >
                                                                                <path fillRule="evenodd" d="M7.646 4.646a.5.5 0 01.708 0l6 6a.5.5 0 01-.708.708L8 5.707l-5.646 5.647a.5.5 0 01-.708-.708l6-6z" clipRule="evenodd" />
                                                                            </svg>
                                                                        )
                                                                    ) : ''}
                                                                </span>
                                                                <div>
                                                                    {column.canFilter ? column.render("Filter") : null}
                                                                </div>
                                                            </th>)
                                                    })}
                                                </tr>)
                                        }
                                        )}
                                    </thead>
                                    {loading ? (
                                        <tbody className="rt-tbody tr-refresh">
                                            {[...Array(page && page.length && page.length < pageSize ? page.length : 10)].map((e, i) => (
                                                <tr key={i}>
                                                    {[...Array(headerGroups.length ? headerGroups[0].headers.length : 1)].map((f, j) => (
                                                        <td className="loading-cell" key={j}>
                                                            <span></span>
                                                        </td>
                                                    ))}
                                                </tr>
                                            ))}
                                            <tr>
                                                <td colSpan="10000">{actions.load}</td>
                                            </tr>
                                        </tbody>
                                    ) : (
                                        <tbody {...getTableBodyProps()} className="rt-tbody">
                                            {page.map((row, i) => {
                                                prepareRow(row);
                                                let { key, ...restRowProps } = row.getRowProps();
                                                return (
                                                    <React.Fragment key={i}>
                                                        <tr key={key} {...restRowProps}>
                                                            {row.cells.map((cell) => {
                                                                let { key, ...restColProps } = cell.getCellProps();
                                                                return (
                                                                    <td key={key} {...restColProps}>{cell.render("Cell")}</td>
                                                                );
                                                            })}
                                                        </tr>
                                                    </React.Fragment>
                                                );
                                            })}
                                            <tr className="result-count">
                                                <td colSpan="10000">Showing {page.length} of {totalCount} results</td>
                                            </tr>
                                        </tbody>
                                    )}
                                </table>
                                {totalCount > 0 && showPagination === true ?
                                    <div className="pagination">
                                        <button className="me-1" onClick={() => gotoPage(0)} disabled={!canPreviousPage}>{"<<"}</button>
                                        <button className="me-1" onClick={() => previousPage()} disabled={!canPreviousPage}>{"<"}</button>
                                        <button className="me-1" onClick={() => nextPage()} disabled={!canNextPage}>{">"}</button>
                                        <button className="me-1" onClick={() => gotoPage(pageCount - 1)} disabled={!canNextPage}>{">>"}</button>
                                        <span className="page-display me-1">Page <strong>{pageIndex >= pageOptions.length ? pageOptions.length : pageIndex + 1} of {pageOptions.length}</strong></span>

                                        <select className="form-select res-dropdown" value={pageSize} onChange={(e) => { handleChangePageSize(Number(e.target.value)); }}>
                                            {[10, 20, 30, 40, 50].map((pageSize) => (
                                                <option key={pageSize} value={pageSize}>Show {pageSize}</option>
                                            ))}
                                        </select>
                                    </div>
                                    : ''}
                            </div>
                        </>
                        : <ErrorMsgBlockComponent noData={true} />}
                </>
            }
        </>
    );
};

const ConfigureCaseListTable = (props) => {
    const [caseDetailsObj, setCaseDetailsObj] = React.useState(null);
    const [showDeleteConfirmation, setDeleteConfirmation] = React.useState(false);
    const [currentPageIndex, setCurrentPageIndex] = React.useState(props.initialState.pageIndex);
    const [currentPageSize, setCurrentPageSize] = React.useState(props.initialState.pageSize);
    const [currentSortBy, setCurrentSortBy] = React.useState(props.initialState.sortBy);
    //eslint-disable-next-line
    var columnsList = [];
    Array.isArray(props.columns) && props.columns.forEach(element => {
        /* istanbul ignore next  */
        if (element.CaseAction) {
            let caseActions = {
                Header: element.Header,
                Cell: ({ row }) => (
                    <div className="case-actions">
                        <Link
                            className="btn-view-case-details"
                            id="view-case"
                            title="Go To Case Details"
                            to={{
                                pathname: `/casedetails/${row.original.caseDetails.caseID}`,
                                state: {
                                    id: row.original.caseDetails.caseID,
                                    surgeonName: row.original.caseDetails.surgeonUserName,
                                    activeTab: 'caseList',
                                    pageIndex: currentPageIndex,
                                    pageSize: currentPageSize,
                                    sortBy: currentSortBy
                                }
                            }}>
                            <svg width="1.7em" height="1.7em" viewBox="0 0 16 16" className="bi bi-eye cursor-pointer" xmlns="http://www.w3.org/2000/svg">
                                <path fillRule="evenodd" d="M16 8s-3-5.5-8-5.5S0 8 0 8s3 5.5 8 5.5S16 8 16 8zM1.173 8a13.134 13.134 0 0 0 1.66 2.043C4.12 11.332 5.88 12.5 8 12.5c2.12 0 3.879-1.168 5.168-2.457A13.134 13.134 0 0 0 14.828 8a13.133 13.133 0 0 0-1.66-2.043C11.879 4.668 10.119 3.5 8 3.5c-2.12 0-3.879 1.168-5.168 2.457A13.133 13.133 0 0 0 1.172 8z" />
                                <path fillRule="evenodd" d="M8 5.5a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5zM4.5 8a3.5 3.5 0 1 1 7 0 3.5 3.5 0 0 1-7 0z" />
                            </svg>
                        </Link>
                        {userHasPermission('caseDelete') ?
                            <span className="case-delete-icon ms-2" title="Delete Case">
                                <svg onClick={() => toggleDeleteCaseModal(row.original)} width="1.7em" height="1.7em" viewBox="0 0 16 16" className="bi bi-trash delete-icon cursor-pointer" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0V6z" />
                                    <path fillRule="evenodd" d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1v1zM4.118 4L4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4H4.118zM2.5 3V2h11v1h-11z" />
                                </svg>
                            </span>
                            : ''}
                    </div>
                ),
            };
            columnsList.push(caseActions);
        } else {
            columnsList.push(element)
        }
    })
    const columns = React.useMemo(() => columnsList, [columnsList]);

    /**
     * @description Function to toggle case delete confirmation modal
     * @param caseDetails Case details row element
     */
    /* istanbul ignore next  */
    const toggleDeleteCaseModal = (caseDetails) => {
        setCaseDetailsObj(caseDetails);
        setDeleteConfirmation(!showDeleteConfirmation)
    }

    /**
     * @description Function to propagate the delete API call and close confirmation modal
     */
    /* istanbul ignore next  */
    const deleteCase = () => {
        props.deleteCase(caseDetailsObj)
        setDeleteConfirmation(false)
    }

    /**
     * @description Function to save the changed page size
     * @param newPageSize Integer value changed from the dropdown
     */
    /* istanbul ignore next  */
    const changePageSize = (newPageSize) => {
        setCurrentPageSize(newPageSize)
    }

    /**
     * @description Function to save the changed pagination params
     * @param params Object with pagination params
     */
    const setPaginationParams = React.useCallback((params) => {
        setCurrentPageSize(params.pageSize);
        setCurrentPageIndex(params.pageIndex);
        setCurrentSortBy(params.sortBy);
        props.fetchData(params)
        // eslint-disable-next-line
    }, [props.fetchData, props.filterObj])

    /**
     * @description Function to get teh case/patient id for the delete case confirmation mdoal
     * @returns Patient ID for THA cases, case ID for other procedures
     */
    /* istanbul ignore next  */
    const getDeleteModalCaseID = () => {
        const caseDetails = caseDetailsObj?.caseDetails;
        const operativeProcedure = caseDetails?.operativeProcedure?.toLowerCase();
        switch (operativeProcedure) {
            case operativeProcedureTypes.TSA.inLowerCase:
            case operativeProcedureTypes.TKA.inLowerCase:
            case operativeProcedureTypes.TKA.text.toLowerCase():
                return caseDetails?.caseID;

            case operativeProcedureTypes.HIP.inLowerCase:
                let caseIDValue = caseDetails?.patientID ? caseDetails?.patientID : caseDetails?.caseID ? caseDetails?.caseID : null;
                return caseIDValue;

            default:
                return caseDetails?.caseID;
        }
    }

    return (
        <>
            <CaseListTable
                initialState={props.initialState}
                columns={columns}
                data={props.data}
                fetchData={setPaginationParams}
                actionErrorObj={props.actionErrorObj}
                loading={props.loading}
                actionLoading={props.actionLoading}
                pageCount={props.pageCount}
                pageNumberIndex={props.pageIndex}
                totalCount={props.totalCount}
                defaultPageSize={props.defaultPageSize}
                showPagination={props.showPagination}
                disableColmSort={props.disableColmSort}
                filterObj={props.filterObj}
                errorObj={props.errorObj}
                refreshList={props.refreshList}
                actionSuccessMsg={props.actionSuccessMsg}
                changePageSize={changePageSize}
            />

            {/* Confirmation modal for deleting a case */}
            <CustomModal
                id="case-delete-modal"
                show={showDeleteConfirmation || props.actionLoading}
                title="Delete Case"
                isConfirm={true}
                loading={props.actionLoading}
                body={parseHTML(surgeonMessages.caseDeleteConfirmation(getDeleteModalCaseID()))}
                dialogClassName="case-delete-dialog"
                closeAction={toggleDeleteCaseModal}
                rejectAction={toggleDeleteCaseModal}
                confirmAction={deleteCase}
            />
        </>

    );
};

export { CaseListTable, ConfigureCaseListTable };